<!-- app-subdomain-creators\src\Components\NewsfeedCreatorScheduledTab.vue -->

<template>
  <div class=" lg:w-[1175px] mx-auto pb-10 pt-5 min-h-[650px]">
    <!-- Loading / error / empty states -->
    <div v-if="loading" class="py-8 text-center text-sm text-zinc-500">
      Loading scheduled posts…
    </div>

    <div v-else-if="errorMessage" class="py-8 text-center text-sm text-red-500">
      {{ errorMessage }}
    </div>

    <!-- Empty State Message -->
    <div
  v-else-if="!scheduledPosts.length"
  class="flex flex-col items-center pt-10 pb-24 text-center"
>
  <div class="flex flex-col items-center -ml-40">
    <!-- Document icon -->
    <div class="mb-4 flex items-center justify-center">
      <DocumentTextIcon class="w-20 h-20 text-zinc-400" />
    </div>

    <!-- Title -->
    <p class="text-base font-semibold text-zinc-800 mb-1">
      No scheduled posts
    </p>

    <!-- Subtitle -->
    <p class="text-sm text-zinc-500 max-w-md mb-4">
      You can schedule posts to be shared to the group at the date and time you choose.
    </p>

    <!-- Create post button -->
    <button
      type="button"
      class="mt-2 inline-flex items-center justify-center px-4 py-2 rounded-md
             bg-[#2D8CFF] text-white text-sm font-semibold
             hover:bg-[#1f7aeb] transition-colors"
      @click="showCreatePostModal = true"
    >
      Create post
    </button>
  </div>
   </div>


    <div v-else class="space-y-4 max-w-[700px]">
      <NewsfeedCreatorScheduledPost
        v-for="post in scheduledPosts"
        :key="post.id"
        :post="post"
        :is-publishing="publishingPostId === post.id"
        :is-saved-context="false"
        @edit-post="handleEditPostFromScheduled"
        @post-now="handlePostNow"
        @reschedule="openReschedule"
        @save-post="handleScheduledSavePost"
        @unsave-post="handleScheduledUnsavePost"
        @delete-post="handleScheduledDeletePost"
        @pin-post="handleScheduledPinPost"
      />

    </div>

    <!-- Reschedule overlay -->
     <Transition
    enter-active-class="ease-out duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="showScheduler && activePost"
      class="fixed inset-0 bg-white bg-opacity-75 flex justify-center items-center py-4 z-50"
    >
    
      <div
        class="bg-white rounded-2xl shadow-xl w-full max-w-[548px] relative"
      >

      <div class="border-b border-zinc-200 py-4 items-center">
        <button
          type="button"
          class="text-zinc-600 hover:text-zinc-800 absolute top-3 right-4 rounded-full bg-zinc-200 hover:bg-zinc-300 p-1"
          @click="closeScheduler"
        >
          <XMarkIcon class="w-7 h-7" />
        </button>

        

        <h3 class="text-lg text-center font-semibold text-zinc-950 ">
          Reschedule post
        </h3>
        </div>
<div class="px-4 py-4">
        <p class="text-sm text-zinc-600 mb-4 ">
          Choose a date and time in the future when you want your post to be published.
        </p>

        <NewsfeedPostScheduler @schedule="handleRescheduleConfirmed" />

        </div>
      </div>

      
    </div>
</Transition>






  </div>

<!-- Create Post modal (for empty-state "Create post" button and also reusable) -->
<CreatePostNewsfeedModal
  :is-open="showCreatePostModal"
  :feature-instance-id="featureInstanceId"
  :posting-as-name="postingAsName"
  :posting-as-avatar-url="postingAsAvatarUrl"
  @close="handleCreatePostClosed"
  @post-created="handlePostCreated"
/>


</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, defineProps } from 'vue';
import NewsfeedCreatorScheduledPost from '@/Components/NewsfeedCreatorScheduledPost.vue';
import NewsfeedPostScheduler from '@/Components/NewsfeedPostScheduler.vue';

import CreatePostNewsfeedModal from '@/Components/CreatePostNewsfeedModal.vue';
import { DocumentTextIcon } from '@heroicons/vue/24/solid';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { useWebsocketStore } from '@/stores/websocketStore';



const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const websocketStore = useWebsocketStore();
let unsubscribeNewsfeedPostDeleted = null;

const props = defineProps({
  featureInstanceId: {
    type: [Number, String],
    required: true,
  },
  // 🔹 mirror Discussion tab inputs
  postingAsName: {
    type: String,
    default: 'You',
  },
  postingAsAvatarUrl: {
    type: String,
    default: null,
  },
});
const emit = defineEmits(['open-edit-post']);

defineExpose({
  reloadPosts: fetchScheduledPosts,
});

const loading = ref(false);
const errorMessage = ref('');
const scheduledPosts = ref([]);

const publishingPostId = ref(null);

const showCreatePostModal = ref(false);

// For reschedule overlay
const showScheduler = ref(false);
const activePost = ref(null);

const scheduledPostTimestamp = (post) => {
  const raw = String(post?.scheduledFor || post?.scheduled_for || '').trim();
  if (!raw) return Number.POSITIVE_INFINITY;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)
    ? `${raw.replace(' ', 'T')}Z`
    : raw;
  const timestamp = Date.parse(normalized);
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

const sortScheduledPosts = (posts) => [...(Array.isArray(posts) ? posts : [])]
  .sort((a, b) => {
    const aScheduled = scheduledPostTimestamp(a);
    const bScheduled = scheduledPostTimestamp(b);
    if (aScheduled !== bScheduled) return aScheduled - bScheduled;
    const aCreated = Date.parse(String(a?.createdAt || a?.created_at || '')) || 0;
    const bCreated = Date.parse(String(b?.createdAt || b?.created_at || '')) || 0;
    return bCreated - aCreated;
  });

const handleEditPostFromScheduled = ({ postId }) => {
  if (!postId) return;
  const postToEdit = scheduledPosts.value.find((p) => String(p.id) === String(postId));
  if (!postToEdit) return;
  emit('open-edit-post', { post: { ...postToEdit } });
};

async function fetchScheduledPosts() {
  if (!props.featureInstanceId) return;

  loading.value = true;
  errorMessage.value = '';

  try {
    const params = new URLSearchParams({
      featureInstanceId: String(props.featureInstanceId),
      limit: '50',
      offset: '0',
    });

    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/my-scheduled-posts?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }

    if (!res.ok || !data || data.success === false) {
      console.error('[ScheduledTab] fetch error:', res.status, data);
      errorMessage.value =
        (data && data.error) ||
        'There was a problem loading scheduled posts.';
      scheduledPosts.value = [];
      return;
    }

    scheduledPosts.value = sortScheduledPosts(data.posts);
  } catch (err) {
    console.error('[ScheduledTab] network error:', err);
    errorMessage.value =
      'Network error while loading scheduled posts. Please try again.';
    scheduledPosts.value = [];
  } finally {
    loading.value = false;
  }
}

function handleWsPostDeleted(payload) {
  if (!payload) return;
  if (String(payload.instanceId || '') !== String(props.featureInstanceId || '')) return;
  scheduledPosts.value = scheduledPosts.value.filter(
    (post) => String(post.id) !== String(payload.postId)
  );
}

onMounted(() => {
  fetchScheduledPosts();
  unsubscribeNewsfeedPostDeleted = websocketStore.subscribe(
    'newsfeed:postDeleted',
    handleWsPostDeleted
  );
});

onBeforeUnmount(() => {
  if (unsubscribeNewsfeedPostDeleted) {
    unsubscribeNewsfeedPostDeleted();
    unsubscribeNewsfeedPostDeleted = null;
  }
});

watch(
  () => props.featureInstanceId,
  () => {
    fetchScheduledPosts();
  }
);

// ---- Post now ----
async function handlePostNow(post) {
  if (!post || !post.id) return;
  if (publishingPostId.value) return; // simple guard

  publishingPostId.value = post.id;

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        post.id
      )}/publish-now`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
      }
    );

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }

    if (!res.ok || !data || data.success === false) {
      console.error('[ScheduledTab] publish-now error:', res.status, data);
      alert(
        (data && data.error) ||
          'There was a problem publishing this post. Please try again.'
      );
      return;
    }

    // Remove from scheduled list (it is now published)
    scheduledPosts.value = scheduledPosts.value.filter(
      (p) => p.id !== post.id
    );

    // (Optional) You could emit an event so the Discussion tab can prepend it
    // emit('post-published-now', data.post);

  } catch (err) {
    console.error('[ScheduledTab] publish-now network error:', err);
    alert(
      'Network error while trying to publish this post. Please try again.'
    );
  } finally {
    publishingPostId.value = null;
  }
}

// ---- Reschedule flow ----
function openReschedule(post) {
  activePost.value = post;
  showScheduler.value = true;
}

function closeScheduler() {
  showScheduler.value = false;
  activePost.value = null;
}

async function handleRescheduleConfirmed(sqlDatetime) {
  if (!activePost.value || !activePost.value.id) return;
  if (!sqlDatetime) return;

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        activePost.value.id
      )}/reschedule`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ scheduledFor: sqlDatetime }),
      }
    );

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }

    if (!res.ok || !data || data.success === false) {
      console.error('[ScheduledTab] reschedule error:', res.status, data);
      alert(
        (data && data.error) ||
          'There was a problem rescheduling this post. Please try again.'
      );
      return;
    }

    const updated = data.post || {};
    const activePostId = activePost.value.id;
    const updatedScheduledFor = updated.scheduledFor || updated.scheduled_for || sqlDatetime;
    scheduledPosts.value = sortScheduledPosts(
      scheduledPosts.value.map((p) =>
        String(p.id) === String(activePostId)
          ? { ...p, ...updated, scheduledFor: updatedScheduledFor }
          : p
      )
    );

    closeScheduler();
  } catch (err) {
    console.error('[ScheduledTab] reschedule network error:', err);
    alert(
      'Network error while trying to reschedule this post. Please try again.'
    );
  }
}

function handleCreatePostClosed() {
  showCreatePostModal.value = false;
}

function handlePostCreated() {
  // Close modal and refresh scheduled list
  showCreatePostModal.value = false;
  fetchScheduledPosts();
}

async function handleScheduledSavePost({ postId }) {
  if (!postId) {
    console.warn('[ScheduledTab] handleScheduledSavePost: no postId in payload');
    return;
  }

  // Child already did the POST /save.
  // Just mark it as saved in local state so the bookmark shows.
  scheduledPosts.value = scheduledPosts.value.map((p) =>
    p.id === postId
      ? {
          ...p,
          isSaved: true,
          is_saved: true,
          saved: true,
        }
      : p
  );
}

async function handleScheduledUnsavePost({ postId }) {
  if (!postId) {
    console.warn('[ScheduledTab] handleScheduledUnsavePost: no postId in payload');
    return;
  }

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}/save`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      console.error('[ScheduledTab] unsave-post backend NOT OK:', res.status, text);
      alert('Failed to unsave post. Please try again.');
      return;
    }

    // Optionally inspect response JSON, but not required
    // const data = await res.json().catch(() => ({}));
  } catch (err) {
    console.error('[ScheduledTab] unsave-post network error:', err);
    alert('Network error while unsaving post. Please try again.');
    return;
  }

  // Clear saved flags locally so bookmark disappears
  scheduledPosts.value = scheduledPosts.value.map((p) =>
    p.id === postId
      ? {
          ...p,
          isSaved: false,
          is_saved: false,
          saved: false,
        }
      : p
  );
}

async function handleScheduledDeletePost(payload) {
  const postId = payload?.postId || payload?.id;

  if (!postId) {
    console.warn(
      '[ScheduledTab] handleScheduledDeletePost: no postId in payload',
      payload
    );
    return;
  }

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/${encodeURIComponent(
        postId
      )}`,
      {
        method: 'DELETE',
        credentials: 'include',
      }
    );

    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }

    if (!res.ok || (data && data.success === false)) {
      console.error('[ScheduledTab] delete error:', res.status, data);
      alert(
        (data && data.error) ||
          'There was a problem deleting this scheduled post. Please try again.'
      );
      return;
    }

    // Remove from scheduled list
    scheduledPosts.value = scheduledPosts.value.filter((p) => p.id !== postId);
  } catch (err) {
    console.error('[ScheduledTab] delete network error:', err);
    alert('Network error while deleting this scheduled post. Please try again.');
  }
}


function handleScheduledPinPost({ postId, isPinned }) {
  if (!postId) {
    console.warn('[ScheduledTab] handleScheduledPinPost: no postId in payload');
    return;
  }

  // 🔹 Only update flags, DO NOT reorder scheduledPosts
  scheduledPosts.value = scheduledPosts.value.map((p) =>
    p.id === postId
      ? {
          ...p,
          isPinned,
          is_pinned: isPinned,
          pinned: isPinned,
        }
      : p
  );
}


</script>

